import { CancelSaveButtons } from '@components/cancel-save-buttons';
import { createForm, useCreateFormStore } from '@components/form';
import { hideModal, Modal, useModal } from '@components/modal';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useUpdateVersionName } from 'api/journeys/hooks';
import { PublishedVersion } from 'api/types';

export const useEditVersionNameModal = () => useModal(EditVersionNameModal);

type FormValues = {
  name: string;
};

const Form = createForm<FormValues>();

export const EditVersionNameModal = ({
  journeyShortId,
  version,
  onSuccess,
}: {
  journeyShortId: string;
  version: PublishedVersion;
  onSuccess?: () => void;
}) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();

  const updateMutation = useUpdateVersionName(journeyShortId);

  const defaultVersionLabel = t('versionNumber', { version: version.versionNumber });

  const handleSubmit = async () => {
    const values = formStore.getState().values as FormValues;
    const trimmedName = values.name.trim();
    // If name is empty or matches default, set to null to use default display
    const nameToSave =
      trimmedName === '' || trimmedName === defaultVersionLabel ? null : trimmedName;
    await updateMutation.mutateAsync({ versionShortId: version.shortId, name: nameToSave });
    hideModal();
    onSuccess?.();
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={t('title')} />
      <Modal.Content>
        <Form store={formStore} onSubmit={handleSubmit}>
          <Form.TextField
            id="name"
            label={t('versionName')}
            defaultValue={version.name ?? ''}
            placeholder={defaultVersionLabel}
            maxChars={255}
          />
          <Text color="textHelper" text={t('description')} />
        </Form>
      </Modal.Content>
      <Modal.Footer>
        <CancelSaveButtons
          alwaysShow
          onCancel={hideModal}
          onSave={handleSubmit}
          translationsOverride={{ save: t('save') }}
          isDisabled={updateMutation.isLoading}
          isSaveDisabled={!formStore.useIsDirty()}
        />
      </Modal.Footer>
    </Modal>
  );
};

export const locale = {
  en: {
    title: 'Rename version',
    versionName: 'Version name',
    versionNumber: 'Version {version}',
    description: 'Leave empty to use the default version name.',
    save: 'Save',
  },
};
